import React, { useState } from 'react';
import StepContainer from '../../components/StepContainer';
import Button from '../../components/Button';
import { useBuildAudience } from '../../context/BuildAudienceContext';
import { UI_TEXT, ERROR_MESSAGES } from '../../config/ui-text';

import { isValidCompanyUrl } from './validateCompanyUrl';
interface AudienceDefineProps {
  onNext: () => void;
  onBack?: () => void;
}

const AudienceDefine: React.FC<AudienceDefineProps> = ({ onNext, onBack }) => {
  const { buildAudienceData, updateBuildAudienceData } = useBuildAudience();

  const [companyUrl, setCompanyUrl] = useState(buildAudienceData.websiteUrl || '');
  // Seeded from the RESTORED value, not blindly true. Now that every keystroke is
  // mirrored into the draft, an in-progress invalid URL like ".com" is persisted — and a
  // reload used to bring it back with isValid still true, so no error rendered and
  // Continue was enabled, sending it straight to the paid build.
  const [isValid, setIsValid] = useState(() =>
    isValidCompanyUrl(buildAudienceData.websiteUrl || ''),
  );

  // The old inline check was `hostname.includes('.') && hostname.length > 3`, which
  // accepts `.com` and `https://.com` and let a meaningless URL reach the paid build
  // (#475 F13-03). See validateCompanyUrl for what is now rejected and why.
  const validateUrl = (url: string) => setIsValid(isValidCompanyUrl(url));

  const handleInputChange = (value: string) => {
    setCompanyUrl(value);
    validateUrl(value);
    // Mirror into the context as it is typed, so the draft has it and a reload BEFORE
    // Continue does not lose it (#475 F13-01) — previously the URL only left this
    // component on Continue.
    //
    // Deliberately without a step number: passing one runs the downstream-invalidation
    // pass, which on every keystroke would clear the segments and approach the user
    // already has. Continue still passes step 1, so the invalidation happens once, when
    // the value is actually committed.
    updateBuildAudienceData({ websiteUrl: value });
  };

  const handleContinue = () => {
    if (isValid && companyUrl.trim()) {
      // Ensure URL has protocol
      let finalUrl = companyUrl.trim();
      if (!finalUrl.startsWith('http')) {
        finalUrl = `https://${finalUrl}`;
      }

      // Save to context
      updateBuildAudienceData(
        {
          websiteUrl: finalUrl,
        },
        1,
      ); // Step 1: websiteUrl input

      onNext();
    }
  };

  return (
    <StepContainer
      title=""
      className="animate-fadeIn flex items-center justify-center min-h-[70vh]"
    >
      <div className="max-w-3xl mx-auto space-y-8 w-full">
        <div className="space-y-6">
          {/* Main Question */}
          <div className="space-y-2">
            <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
              {UI_TEXT.TITLES.COMPANY_INPUT_QUESTION}
            </h1>
            <p className="text-base font-light text-gray-400 leading-relaxed">
              {UI_TEXT.SUBTITLES.COMPANY_INPUT_DESC}
            </p>
          </div>

          {/* Input Field */}
          <div className="space-y-3">
            <input
              type="text"
              className={`w-full px-0 py-2 bg-transparent border-0 border-b-2 transition-all duration-200 text-2xl font-light placeholder:text-sm placeholder-gray-300 outline-none ring-0 focus:ring-0 focus:outline-none ${
                companyUrl && isValid
                  ? 'border-b-primary'
                  : companyUrl && !isValid
                    ? 'border-b-red-400'
                    : 'border-b-gray-200 hover:border-b-gray-300'
              } focus:border-b-primary`}
              placeholder={UI_TEXT.PLACEHOLDERS.COMPANY_INPUT}
              value={companyUrl}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValid) {
                  handleContinue();
                }
              }}
            />

            {/* Validation Message */}
            {companyUrl && !isValid && (
              <p className="text-sm font-light text-red-400">{ERROR_MESSAGES.INVALID_URL}</p>
            )}
          </div>
        </div>

        {/* Navigation buttons - right below the input */}
        <div className="flex items-center space-x-4">
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={!isValid || !companyUrl.trim()}
            className="font-light"
            withArrow={false}
          >
            {UI_TEXT.BUTTONS.CONTINUE}
          </Button>
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600 font-light"
          >
            {UI_TEXT.BUTTONS.BACK}
          </Button>
        </div>
      </div>
    </StepContainer>
  );
};

export default AudienceDefine;
